/* import __COLOCATED_TEMPLATE__ from './conversation-metadata.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type { FinVoiceIntentClassification } from 'embercom/objects/ai-agent/fin-voice/fin-voice-server-events';

export interface ConversationMetadataArgs {
  customDataAttributes?: Record<string, string | number | boolean>;
  intentClassification?: FinVoiceIntentClassification[];
  customDataTestId?: string;
  intentTestId?: string;
}

interface Signature {
  Args: ConversationMetadataArgs;
  Element: HTMLElement;
}

export default class ConversationMetadata extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked isCustomDataExpanded = false;
  @tracked isIntentClassificationExpanded = false;

  get hasCustomData() {
    return this.customDataCount > 0;
  }

  get customDataCount() {
    if (!this.args.customDataAttributes) {
      return 0;
    }

    return Object.keys(this.args.customDataAttributes).length;
  }

  get customDataEntries() {
    if (!this.hasCustomData) {
      return {};
    }

    return this.args.customDataAttributes!;
  }

  get hasIntentClassification() {
    return this.intentClassificationCount > 0;
  }

  get intentClassificationCount() {
    if (!this.args.intentClassification) {
      return 0;
    }

    return this.args.intentClassification.length;
  }

  get intentClassificationItems() {
    if (!this.hasIntentClassification) {
      return [];
    }

    return this.args.intentClassification!.map((intent) => ({
      id: intent.attribute.id,
      name: intent.attribute.name,
      linkedAttributeId: intent.attribute.linked_attribute_id || 'null',
    }));
  }

  @action
  toggleCustomData() {
    this.isCustomDataExpanded = !this.isCustomDataExpanded;
  }

  @action
  toggleIntentClassification() {
    this.isIntentClassificationExpanded = !this.isIntentClassificationExpanded;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::VoicePlayground::ConversationItems::ConversationMetadata': typeof ConversationMetadata;
  }
}
